import { Table, Form, Button, Checkbox, Popover, Popconfirm, Modal } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/app.hooks';
import { toast } from 'react-toastify';
import { IDataTable } from './dataTable.model';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants/common';
import _ from 'lodash';
import { Filter } from './DataTableFilters';
import {
  fixedColumn,
  IInlineSearch,
  ISearch,
  ITableColumnSelection,
  orderByType,
} from '../../../common/models/common';
import {
  clearBulkActionConfirmation,
  clearBulkDeleteMessage,
  clearCronJobSchedularMessages,
  clearSaveTableColumnSelection,
  commonSelector,
} from '../../../store/common/common.reducer';
import { ExclamationCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import { bulkDelete, saveTableColumnSelection } from '../../../store/common/common.action';
import { globalSearchSelector } from '../../../store/globalSearch/globalSearch.reducer';
import ReactDragListView from 'react-drag-listview';
import { spsApiCallSelector } from '../../../store/sps/spsAPICall/spsApiCall.reducer';
import { startAll } from '../../../store/master/cron/cron.action';
import {
  BulkActionType,
  IBulkDelete,
  IBulkImpact,
  IBulkImpactRequest,
} from '../../../services/common/common.model';
import commonService from '../../../services/common/common.service';
import { convertDateRangeFilters, getBulkSelectionPayload } from '../../helperFunction';
import BulkImpactConfirmModal from './BulkImpactConfirmModal';
import { getExportWarnings } from './exportGuard';

let pageLoaded = false;
const DataTable: React.ForwardRefRenderFunction<unknown, IDataTable> = (props, ref) => {
  const {
    defaultOrderBy,
    showAddButton,
    showBulkUpdate,
    setShowSelectedListModal,
    globalSearchExist,
    extraSearchData,
    setSelectedId,
    getTableColumns,
    reduxSelector,
    tableAction,
    tableButtons,
    searchTableData,
    clearTableDataMessages,
    exportExcelFile,
    setTableColumnSelection,
    hideExportButton,
    showCallApiBtn,
    onCallAllApi,
    setValuesForSelection,
    setNumberOfRecords,
    disableRowSelection,
    setObjectForColumnFilter,
    isStartSchedulaAllApi,
    hideShowHideButton,
    type_id,
    isExcelColumnMapping,
    isSpsApiJobsId,
    setDropDownFlag,
    dropDownFlag,
    filterKeysDD,
    filterRecordsForLocalSearch,
    showDelete,
    isForeignLookup,
    removeSearch,
    isRadio,
    isSpsJobs,
    isIdFiltered
  } = props;

  const tableFilter = useRef({
    keyword: '',
    order_by: 'id',
    order_direction: 'DESC' as orderByType,
    filter_keys: {},
  });

  const reduxStoreData = useAppSelector(reduxSelector);
  const common = useAppSelector(commonSelector);
  const globalFilters = useAppSelector(globalSearchSelector);
  const spsApisState = useAppSelector(spsApiCallSelector);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [inlineSearch, setInlineSearch] = useState<IInlineSearch>({});
  const [indeterminate, setIndeterminate] = React.useState(false);
  const [checkAll, setCheckAll] = React.useState(false);
  const [isDragged, setIsDragged] = React.useState(false);
  const [tableColumns, setTableColumns] = React.useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [selectedRowList, setSelectedRowList] = React.useState<any[]>([]);
  const [filterForConfirm, setFilterForConfirm] = React.useState<any>({});
  // Bulk Update/Delete All guard: impact check -> existing Popconfirm (single dataset) or
  // typed-confirmation danger modal (multiple datasets).
  const [confirmOpen, setConfirmOpen] = React.useState<BulkActionType | null>(null);
  const [impactLoading, setImpactLoading] = React.useState<BulkActionType | null>(null);
  const [dangerImpact, setDangerImpact] = React.useState<{
    action: BulkActionType;
    impact: IBulkImpact;
    serverMessage?: string;
    isRetry?: boolean;
  } | null>(null);
  // confirm_text accepted in the danger modal for Update All; carried into the update modal's
  // payload through getValuesForSelection -> getObjectForUpdateMultiple.
  const updateConfirmTextRef = useRef<string | null>(null);
  const lastBulkActionRef = useRef<BulkActionType | null>(null);

  tableFilter.current.order_by = defaultOrderBy ? defaultOrderBy : tableFilter.current.order_by;

  //For checked List of Rows Selection
  const rowSelection: any = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowList({
        ...selectedRowKeys,
        selectedRowList: selectedRowKeys
      });
    },
    type: isRadio === true ? 'radio' : 'checkbox',
  };

  // Builds the grid search request without side effects. getSearchData() (grid search and
  // export) and getEffectiveSearchFilterKeys() (bulk delete/update/impact) both use it, so
  // bulk actions target exactly the rows the grid search returned.
  const buildSearchData = (page, isExportToExcel: boolean) => {
    const { filter_keys, ...rest } = tableFilter.current;

    if (!page) {
      page = pagination;
    }

    const inlineSearchFilter = _.pickBy(filter_keys, function (value) {
      return !(
        value === undefined ||
        value === '' ||
        _.isNull(value) ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    const inlineSearchFilterObj = convertDateRangeFilters(inlineSearchFilter);

    const searchData: ISearch = {
      is_lookup: isForeignLookup === true || isSpsJobs ? true : false,
      is_column_selection: !pageLoaded,
      limit: isStartSchedulaAllApi === true ? undefined : page.pageSize,
      offset: isStartSchedulaAllApi === true ? undefined : (page.current - 1) * page.pageSize,
      ...(rest || {}),
      filter_keys: type_id ? { api_type_id: [type_id] } : inlineSearchFilterObj,
      is_export_to_excel: isExportToExcel,
      ...(extraSearchData || {}),
    };
    return { searchData, inlineSearchFilter };
  };

  const getSearchData = (page, isExportToExcel: boolean) => {
    const { searchData, inlineSearchFilter } = buildSearchData(page, isExportToExcel);
    setInlineSearch(inlineSearchFilter);
    pageLoaded = true;
    return searchData;
  };

  // Page-specific filters the grid search merges into filter_keys.
  const applyExtraFilterKeys = (searchData: ISearch) => {
    //For Pre Select Filtered IDs.
    if (isIdFiltered === true) {
      searchData.filter_keys = { ...searchData.filter_keys, ...extraSearchData };
    }
    //setCallColumnApi(true);
    if (isExcelColumnMapping === true) {
      searchData.filter_keys = { ...searchData.filter_keys, ...extraSearchData };
    }
    //For Sps Api Jobs Filter
    if (isSpsApiJobsId === true) {
      searchData.filter_keys = { ...searchData.filter_keys, ...extraSearchData };
    }
  };

  // filter_keys exactly as fetchTableData sends them to the grid search.
  const getEffectiveSearchFilterKeys = () => {
    const { searchData } = buildSearchData(pagination, false);
    applyExtraFilterKeys(searchData);
    return searchData.filter_keys;
  };

  const fetchTableData = (page = null) => {
    const searchData = getSearchData(page, false);
    searchData;
    if (setObjectForColumnFilter) {
      let filterDate;
      if (isSpsApiJobsId || isExcelColumnMapping) {
        filterDate = {
          ...searchData.filter_keys,
          ...extraSearchData
        }
      } else {
        filterDate = {
          ...searchData.filter_keys,
        }
      }
      setFilterForConfirm(filterDate);
      setObjectForColumnFilter({
        filter_keys: filterDate,
        keyword: searchData.keyword,
        limit: searchData.limit,
        offset: searchData.offset,
        is_column_selection: searchData.is_column_selection,
        order_by: tableFilter.current.order_by,
        order_direction: tableFilter.current.order_direction,
        column_called: [],
      });
    }
    applyExtraFilterKeys(searchData);
    dispatch(searchTableData(searchData));
  };
  const buildValuesForSelection = () => {
    const Obj = {
      isLookup: !pageLoaded,
      // Same effective filters as the grid search, so Update All covers exactly the rows shown.
      filterKeys: getEffectiveSearchFilterKeys(),
      is_export_to_excel: !pageLoaded,
      limit: 10,
      offset: 0,
      order_by: tableFilter.current.order_by,
      order_direction: tableFilter.current.order_direction,
      keyword: tableFilter.current.keyword,
    };
    Obj['selectedIds'] = selectedRowList;
    if (updateConfirmTextRef.current) {
      Obj['confirm_text'] = updateConfirmTextRef.current;
    }
    return Obj;
  };

  useImperativeHandle(ref, () => ({
    refreshData() {
      fetchTableData();
    },
    getValuesForSelection() {
      setValuesForSelection(buildValuesForSelection());
    },
    getNumberOfRecordsForUpdate() {
      if (Object.keys(selectedRowList).length <= 1) {
        setNumberOfRecords(reduxStoreData.search.count);
      } else {
        setNumberOfRecords(Object.keys(selectedRowList).length - 1);
      }
    },
  }));

  React.useEffect(() => {
    return () => {
      //For RowSelection in Table
      pageLoaded = false;
      tableFilter.current = {
        keyword: '',
        order_by: 'id',
        order_direction: 'DESC' as orderByType,
        filter_keys: {},
      };
    };
  }, []);

  // Start: Global Search
  React.useEffect(() => {
    const isGlobalSearchExist: boolean = globalSearchExist === undefined ? true : globalSearchExist;
    if (isGlobalSearchExist) {
      const globalSearch: IInlineSearch = {};
      for (const key in globalFilters.search) {
        const element = globalFilters.search[key];
        globalSearch[key] = element ? [element] : null;
      }
      tableFilter.current.filter_keys = { ...tableFilter.current.filter_keys, ...globalSearch };
      setPagination({ ...pagination, current: 1 });
      fetchTableData({ ...pagination, current: 1 });
    }
  }, [globalFilters.search]);

  React.useEffect(() => {
    const isGlobalSearchExist: boolean = globalSearchExist === undefined ? true : globalSearchExist;
    if (!isGlobalSearchExist) {
      setPagination({ ...pagination, current: 1 });
      fetchTableData({ ...pagination, current: 1 });
    }
  }, []);
  // End: Global Search

  // Start: Pagination ans Sorting
  const handleTableChange = (paginating, filters, sorter) => {
    if (!isStartSchedulaAllApi) {
      tableFilter.current = {
        ...tableFilter.current,
        order_by:
          sorter.field ||
          sorter.column?.children[0]?.dataIndex ||
          (defaultOrderBy ? defaultOrderBy : 'id'),
        order_direction: (sorter.order === 'ascend' ? 'ASC' : 'DESC') as orderByType,
      };
      setPagination(paginating);
      fetchTableData(paginating);
    }
  };

  React.useEffect(() => {
    if (reduxStoreData.delete?.messages && reduxStoreData.delete?.messages.length > 0) {
      if (reduxStoreData.delete.hasErrors) {
        toast.error(reduxStoreData.delete?.messages.join(' '));
      } else {
        toast.success(reduxStoreData.delete?.messages.join(' '));
        fetchTableData();
      }
      dispatch(clearTableDataMessages());
    }
  }, [reduxStoreData?.delete?.messages]);

  React.useEffect(() => {
    if (common.saveTableColumnSelection?.messages && common.saveTableColumnSelection?.messages.length > 0) {
      if (common.saveTableColumnSelection.hasErrors) {
        toast.error(common.saveTableColumnSelection?.messages.join(' '));
      } else {
        toast.success(common.saveTableColumnSelection?.messages.join(' '));
      }
      dispatch(clearSaveTableColumnSelection());
    }
  }, [common?.saveTableColumnSelection?.messages]);

  React.useEffect(() => {
    if (common.bulkDelete?.messages && common.bulkDelete?.messages.length > 0) {
      if (common.bulkDelete.hasErrors) {
        toast.error(common.bulkDelete?.messages.join(' '));
      } else {
        toast.warn(common.bulkDelete?.messages.join(' '));
      }
      dispatch(clearBulkDeleteMessage());
    }
  }, [common?.bulkDelete?.messages]);

  React.useEffect(() => {
    if (common.manageCronJob?.messages && common.manageCronJob?.messages.length > 0) {
      if (common.manageCronJob.hasErrors) {
        toast.error(common.manageCronJob?.messages.join(' '));
      } else {
        toast.success(common.manageCronJob?.messages.join(' '));
      }
      dispatch(clearCronJobSchedularMessages());
    }
  }, [common.manageCronJob.messages]);
  // End: Delete action

  // Keyword search
  const onFinishSearch = (value: string) => {
    tableFilter.current = {
      ...tableFilter.current,
      keyword: value,
    };
    setPagination({ ...pagination, current: 1 });
    fetchTableData({ ...pagination, current: 1 });
  };

  // Start: Column level filter
  const onFinish = (values: IInlineSearch) => {
    tableFilter.current.filter_keys = values;
    setPagination({ ...pagination, current: 1 });
    fetchTableData({ ...pagination, current: 1 });
  };
  const onReset = () => {
    if (setDropDownFlag) setDropDownFlag(false);
    const globalSearch: IInlineSearch = {};
    for (const key in globalFilters.search) {
      const element = globalFilters.search[key];
      globalSearch[key] = element ? [element] : null;
    }
    if (
      globalFilters.search.tenant_id &&
      globalFilters.search.tenant_id !== 0 &&
      !(globalSearchExist == false)
    ) {
      const initlValues = {
        company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
        bu_id: _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0],
        tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
      };
      form.setFieldsValue(initlValues);
      if (globalFilters.search) {
        onFinish(initlValues);
      }
    } else {
      onFinish({});
    }
  };
  React.useEffect(() => {
    form.resetFields();
  }, [inlineSearch]);
  // End: Column level filter

  // Export Excel
  const downloadExcel = (presetSearchData?: ISearch) => {
    const exArr: any[] = getColumns();
    setLoading(true);
    const export_column_details: Array<{ key: string; index: any; label: string }> = [];
    exArr.map((column, index) => {
      if (column?.children[0]?.key !== 'Actions') {
        export_column_details.push({
          key: column?.children[0]?.key,
          index: index + 1,
          label: column.title.props?.children,
        });
      }
    });
    const searchData = presetSearchData || getSearchData(pagination, true);
    searchData.is_lookup = isSpsJobs ? true : false;
    searchData.limit = 0;
    const exportData = {
      ...searchData,
      export_column_details,
      total_records: reduxStoreData.search.count,
      table_name: reduxStoreData.search.tableName,
    };

    return exportExcelFile(exportData).then((res) => {
      if (res && res?.data?.body?.messages) {
        toast.warning(res.data.body.messages.join(' '));
      } else {
        toast.error('Document not available.');
      }
      setLoading(false);
    });
  };

  // Confirm before exporting large / multi-dataset results
  const onExportClick = () => {
    const count = Number(reduxStoreData.search.count) || 0;
    // Judge exactly what the export request will send
    const exportSearchData = getSearchData(pagination, true);
    if (count === 0) {
      downloadExcel(exportSearchData);
      return;
    }
    // Admin/master tables (no Tenant/Company/BU selector) only get the row-count rule.
    // Dimension detection uses the full column list (before Show/Hide filtering).
    const checkDatasets = globalSearchExist !== false;
    const columnKeys = checkDatasets
      ? (columns || []).map((column: any) => column?.children?.[0]?.key ?? column?.column)
      : [];
    const warnings = getExportWarnings(count, exportSearchData.filter_keys, columnKeys);
    if (warnings.length === 0) {
      downloadExcel(exportSearchData);
      return;
    }
    Modal.confirm({
      title: `Export ${count.toLocaleString('en-US')} rows?`,
      icon: <ExclamationCircleOutlined />,
      centered: true,
      content: (
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ),
      okText: 'Export',
      cancelText: 'Cancel',
      onOk: () => downloadExcel(exportSearchData),
    });
  };

  // Table columns
  let columns = [
    ...(getTableColumns(form) || []),
    {
      title: 'Actions',
      column: 'Actions',
      children: [
        {
          title: (
            <div className="btns-block">
              <Button
                htmlType="submit"
                className={`action-btn filter-btn p-0 ${_.every(inlineSearch, _.isEmpty) ? '' : 'active'
                  }`}
              >
                <img src={`${process.env.PUBLIC_URL}/assets/images/ic-filter.svg`} alt="" />
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/ic-filter-white.svg`}
                  className="ovarlap"
                  alt=""
                />
              </Button>
              <Button htmlType="button" onClick={onReset} className="action-btn filter-btn p-0">
                <img src={`${process.env.PUBLIC_URL}/assets/images/ic-clean.svg`} alt="" />
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/ic-clean-white.svg`}
                  className="ovarlap"
                  alt=""
                />
              </Button>
            </div>
          ),
          key: 'Actions',
          width: '80px',
          fixed: 'right' as fixedColumn,
          render: tableAction ? tableAction : () => <></>,
        },
      ],
    },
  ];

  React.useEffect(() => {
    setTableColumns(columns);
  }, [reduxStoreData.tableColumnSelection.table_name]);

  React.useEffect(() => {
    handleIndeterminate();
  }, [reduxStoreData.tableColumnSelection.columns]);

  // Start: Hide-show columns
  const hideShowColumn = (e, title) => {
    let tableColumnsObj: { [key: string]: boolean } = reduxStoreData.tableColumnSelection.columns;
    if (Object.keys(tableColumnsObj)?.length === 0) {
      columns?.forEach((col) => {
        tableColumnsObj = {
          ...tableColumnsObj,
          [col.column ? col.column : col.title]: true,
        };
      });
    }
    if (e.target.checked) {
      dispatch(setTableColumnSelection({ ...tableColumnsObj, [title]: true }));
    } else {
      dispatch(setTableColumnSelection({ ...tableColumnsObj, [title]: false }));
    }
    handleIndeterminate();
  };

  const handleIndeterminate = () => {
    const selectedColumns = columns
      .filter((col) => reduxStoreData.tableColumnSelection.columns[col.column] !== false)
      .map((x) => x.title);
    setIndeterminate(!!selectedColumns.length && selectedColumns.length < columns.length);
    setCheckAll(selectedColumns.length === columns.length);
  };

  const saveTableColumns = () => {
    let tableColumnSelectionObj: ITableColumnSelection = reduxStoreData.tableColumnSelection;
    columns?.forEach((col, index) => {
      tableColumnSelectionObj = {
        ...tableColumnSelectionObj,
        column_orders: {
          ...tableColumnSelectionObj.column_orders,
          [col.column ? col.column : col.title]: index,
        },
      };
    });

    if (Object.keys(tableColumnSelectionObj.columns)?.length === 0) {
      columns?.forEach((col) => {
        tableColumnSelectionObj = {
          ...tableColumnSelectionObj,
          columns: {
            ...tableColumnSelectionObj.columns,
            [col.column ? col.column : col.title]: true,
          },
        };
      });
    } else {
      const isAllDeselected = Object.values(tableColumnSelectionObj.columns).every(
        (col) => col === false
      );
      if (isAllDeselected) {
        toast.info('Please select any column.');
        return false;
      }
    }

    dispatch(saveTableColumnSelection(tableColumnSelectionObj)).then(() => {
      if (!tableColumnSelectionObj.id) {
        pageLoaded = false;
        fetchTableData();
      }
    });
  };

  const handleSelectAllChange = (e) => {
    let selectedColumns: { [key: string]: boolean } = {};
    setIndeterminate(false);
    setCheckAll(e.target.checked);
    columns.forEach((col) => {
      selectedColumns = { ...selectedColumns, [col.column]: e.target.checked };
    });
    dispatch(setTableColumnSelection(selectedColumns));
  };

  const renderColumnNames = () => {
    const names = [...columns];
    return names
      ?.sort((a, b) => a.column?.localeCompare(b.column))
      ?.map((col, i) => (
        <li key={`li-${i}-${col.column}`}>
          <Checkbox
            checked={
              col.column in reduxStoreData.tableColumnSelection.columns
                ? reduxStoreData.tableColumnSelection.columns[col.column]
                : true
            }
            onClick={(e) => hideShowColumn(e, col.column)}
          >
            {col.title}
          </Checkbox>
        </li>
      ));
  };
  const dropdownMenu = (
    <div className="checkbox-list-wrapper">
      <ul className="checkbox-list">
        <li className="line-bottom">
          <Checkbox
            className="strong"
            checked={checkAll}
            onClick={handleSelectAllChange}
            indeterminate={indeterminate}
          >
            Select All
          </Checkbox>
        </li>
        {renderColumnNames()}
      </ul>
      <div className="bottom-fix">
        <Button
          type="primary"
          className="w-100"
          loading={common.saveTableColumnSelection.loading}
          onClick={saveTableColumns}
        >
          Save
        </Button>
      </div>
    </div>
  );

  // End: Hide-show columns
  const dragProps = {
    onDragEnd(fromIndex, toIndex) {
      const visibleColumns = columns?.filter((col) => {
        return col.column in reduxStoreData.tableColumnSelection.columns
          ? reduxStoreData.tableColumnSelection.columns[col.column]
          : true;
      });

      fromIndex = tableColumns.findIndex((x) => x.column === visibleColumns[fromIndex]?.column);
      toIndex = tableColumns.findIndex((x) => x.column === visibleColumns[toIndex]?.column);
      const updatedColumns = [...tableColumns];
      if (disableRowSelection) {
        const item = updatedColumns.splice(fromIndex, 1)[0];
        updatedColumns.splice(toIndex, 0, item);
      } else {
        const item = updatedColumns.splice(fromIndex - 1, 1)[0];
        updatedColumns.splice(toIndex - 1, 0, item);
      }
      setTableColumns(updatedColumns);
      setIsDragged(true);
      setTimeout(() => {
        setIsDragged(false);
      });
    },
    nodeSelector: 'th',
    handleSelector: '.dragHandler',
  };

  useEffect(() => {
    if (reduxStoreData?.tableColumnSelection?.column_orders) {
      const sortedOrder = Object.keys(reduxStoreData?.tableColumnSelection?.column_orders)?.sort(
        function (a, b) {
          return (
            reduxStoreData?.tableColumnSelection?.column_orders[a] -
            reduxStoreData?.tableColumnSelection?.column_orders[b]
          );
        }
      );

      const remainingColumns = [];
      let updatedColumns = sortedOrder?.map((a) =>
        tableColumns.find((x) =>
          x.column
            ? x.column.toString().toLowerCase() === a.toString().toLowerCase()
            : x.title === a
        )
      );
      updatedColumns = updatedColumns.filter((data) => data !== undefined);
      for (const key in tableColumns) {
        const element = tableColumns[key];
        const flagColumn = updatedColumns.filter(
          (x) => x?.column.toString().toLowerCase() === element?.column.toString().toLowerCase()
        );
        if (flagColumn && flagColumn.length < 1) {
          remainingColumns.push(element);
        }
      }
      let updatedVar = [];
      if (remainingColumns.length) {
        updatedVar = [...remainingColumns, ...updatedColumns];
        setTableColumns(updatedVar);
      } else {
        setTableColumns(updatedColumns);
      }
    }
  }, [reduxStoreData?.tableColumnSelection?.column_orders]);

  React.useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedRowList([]);
  }, [reduxStoreData.search]);

  const getColumns = () => {
    if (tableColumns.length > 0) {
      columns = tableColumns.map((i) => columns?.find((j) => j?.column === i?.column));
    }
    return columns?.filter((col) => {
      return col?.column in reduxStoreData.tableColumnSelection.columns
        ? reduxStoreData.tableColumnSelection.columns[col.column]
        : true;
    });
  };

  const onRowSelection = () => {
    if (onCallAllApi) {
      onCallAllApi(tableFilter.current);
    }
  };

  const buildBulkDeletePayload = (): IBulkDelete => {
    const Obj: IBulkDelete = {
      // Same effective filters as the grid search (date ranges as {start_date, end_date},
      // type_id / extraSearchData applied), so Delete All removes exactly the rows shown.
      filter_keys: getEffectiveSearchFilterKeys(),
      is_export_to_excel: !pageLoaded,
      limit: 10,
      offset: 0,
      order_by: tableFilter.current.order_by,
      order_direction: tableFilter.current.order_direction,
      keyword: tableFilter.current.keyword,
      table_name: reduxStoreData.search.tableName,
    };
    const list: any = {
      ...selectedRowList,
    };
    const data = list?.selectedRowList?.map((x) => (x = x?.split('-')[0]));
    Obj['selectedIds'] = data;
    return Obj;
  };

  const bulkDeleteData = (confirmText?: string) => {
    const Obj = buildBulkDeletePayload();
    if (confirmText) {
      Obj.confirm_text = confirmText;
    }
    dispatch(bulkDelete(Obj));
  };

  // Same selection payload the bulk action itself will send, plus the action.
  const buildBulkImpactRequest = (action: BulkActionType): IBulkImpactRequest => {
    if (action === 'delete') {
      return { ...buildBulkDeletePayload(), action };
    }
    const selection = getBulkSelectionPayload(
      buildValuesForSelection(),
      reduxStoreData.search.tableName
    );
    delete selection.confirm_text;
    return { ...selection, action };
  };

  // Entry point for the Delete All/Selected and Update All/Selected buttons, and for a retry
  // after the server refused the action with HTTP 409 (data changed / confirmation stale).
  const startBulkAction = async (action: BulkActionType, serverMessage?: string, isRetry?: boolean) => {
    lastBulkActionRef.current = action;
    if (!isRetry) {
      updateConfirmTextRef.current = null;
    }
    setConfirmOpen(null);
    setImpactLoading(action);
    let impact: IBulkImpact | null = null;
    try {
      const res = await commonService.bulkImpact(buildBulkImpactRequest(action));
      impact = res?.body?.data ?? null;
    } catch (e) {
      // The request interceptor already shows the error. The server still enforces the
      // multi-dataset confirmation (HTTP 409), so fall back to the regular confirmation.
      impact = null;
    } finally {
      setImpactLoading(null);
    }
    if (impact && !impact.single_dataset && impact.confirm_text) {
      setDangerImpact({ action, impact, serverMessage, isRetry });
      return;
    }
    if (isRetry && action === 'update') {
      // The update modal is still open: refresh its selection without a confirmation.
      updateConfirmTextRef.current = null;
      setValuesForSelection?.(buildValuesForSelection());
      toast.info('Selection re-checked. Click Save again to apply the update.');
      return;
    }
    setConfirmOpen(action);
  };

  const onDangerConfirm = () => {
    if (!dangerImpact) {
      return;
    }
    const { action, impact, isRetry } = dangerImpact;
    setDangerImpact(null);
    if (action === 'delete') {
      bulkDeleteData(impact.confirm_text);
      return;
    }
    updateConfirmTextRef.current = impact.confirm_text;
    if (isRetry) {
      // The update modal is still open: hand it the new confirmation.
      setValuesForSelection?.(buildValuesForSelection());
      toast.info('Confirmation recorded. Click Save again to apply the update.');
    } else {
      setShowSelectedListModal(true);
    }
  };

  // bulk-update / bulk-delete refused with HTTP 409: show the server message (already toasted
  // by the request interceptor) in a re-run of the impact check.
  React.useEffect(() => {
    const pending = common.bulkActionConfirmation;
    if (!pending) {
      return;
    }
    dispatch(clearBulkActionConfirmation());
    if (lastBulkActionRef.current === pending.action) {
      startBulkAction(pending.action, pending.message, true);
    }
  }, [common?.bulkActionConfirmation]);

  const renderCallApiButton = () => {
    if (showCallApiBtn) {
      return (
        <Button
          loading={spsApisState.callAllApi.loading}
          className="btn-icon"
          onClick={() => {
            if (isStartSchedulaAllApi) {
              const inlineSearchFilter = _.pickBy(
                tableFilter.current.filter_keys,
                function (value) {
                  return !(
                    value === undefined ||
                    value === '' ||
                    _.isNull(value) ||
                    (Array.isArray(value) && value.length === 0)
                  );
                }
              );
              const Obj = {
                isLookup: !pageLoaded,
                filterKeys: dropDownFlag === true ? { ...filterKeysDD } : inlineSearchFilter,
                is_export_to_excel: !pageLoaded,
                limit: 10,
                offset: 0,
                order_by: tableFilter.current.order_by,
                order_direction: tableFilter.current.order_direction,
                keyword: tableFilter.current.keyword,
              };
              setDropDownFlag(false);
              dispatch(startAll(Obj));
            } else {
              onRowSelection();
            }
          }}
        >
          {isStartSchedulaAllApi ? <>Schedule All</> : <>Call All API</>}
        </Button>
      );
    }
  };

  const getDistinct = (data, key) => {
    const temp = {};
    return data
      .filter(item => {
        const value = item[key];
        if (!temp[value]) {
          temp[value] = true;
          return true;
        }
        return false;
      })
      .map(item => item[key]);
  };


  //This is for pop confirmation on Delete/Update All
  const deleteUpdateTitle = (isUpdate?: boolean) => {
    const list: any = {
      ...selectedRowList,
    };
    const selectedIds = list?.selectedRowList?.map((x) => (x = x?.split('-')[0]));
    const selectedRecords = selectedIds?.length > 0 ? reduxStoreData.search.data.filter((x) => selectedIds?.includes(x.id.toString())) : [];
    const inlineSearchFilter = _.pickBy(filterForConfirm, function (value) {
      return !(
        value === undefined ||
        value === '' ||
        _.isNull(value) ||
        (Array.isArray(value) && value.length === 0)
      );
    });
    const tenants = selectedRecords?.length > 0 ? getDistinct(selectedRecords, 'tenant_name') :
      (getDistinct((common.tenantData.data.length > globalFilters.globalTenantLookup.data.length ?
        common.tenantData.data :
        globalFilters.globalTenantLookup.data).
        filter((x) => Array.isArray(inlineSearchFilter?.tenant_id) ? inlineSearchFilter?.tenant_id?.includes(x.id) : inlineSearchFilter?.tenant_id == x.id), 'name'))
        .filter((x) => x !== undefined);
    const companies = selectedRecords?.length > 0 ? getDistinct(selectedRecords, 'company_name') :
      (getDistinct((common.companyData.data.length > globalFilters.globalCompanyLookup.data.length ?
        common.companyData.data :
        globalFilters.globalCompanyLookup.data).filter((x) => Array.isArray(inlineSearchFilter?.company_id) ?
          inlineSearchFilter?.company_id?.includes(x.id) :
          inlineSearchFilter?.company_id == x.id), 'name'))
        .filter((x) => x !== undefined);
    const bus = selectedRecords?.length > 0 ? getDistinct(selectedRecords, 'bu_name') :
      (inlineSearchFilter?.bu_id?.length > 0 ? (getDistinct((common.buData.data.length > globalFilters.globalBULookup.data.length ?
        common.buData.data :
        globalFilters.globalBULookup.data).filter((x) => Array.isArray(inlineSearchFilter?.bu_id) ? inlineSearchFilter?.bu_id?.includes(x.id) : inlineSearchFilter?.bu_id == x.id), 'name'))
        .filter((x) => x !== undefined) : []);
    const dates = selectedRecords?.length > 0 ? getDistinct(selectedRecords, 'date_added') : (inlineSearchFilter?.date_added || []);
    const tenantNames = tenants.slice(0, 2).join(', ') + (tenants.length > 2 ? ', ...' : '');
    const companyNames = companies.slice(0, 2).join(',') + (companies.length > 2 ? ', ...' : '');
    const buNames = bus.slice(0, 2).join(',') + (bus.length > 2 ? ', ...' : '');
    const dateNames = dates.slice(0, 2).join(',') + (dates.length > 2 ? ', ...' : '');
    return (
      <>
        Are you sure you would like to {isUpdate ? 'update' : 'delete'} {Object.keys(selectedRowList).length <= 1 ?
          (reduxStoreData.search.count).toLocaleString() :
          Object.keys(selectedRowList).length - 1} records ? This impacts the following:
        {globalSearchExist !== false ? (<><br />
          {/* <Tooltip title={tenantNames}> */}
            <span style={{ fontWeight: "bold" }}>Tenant: </span>
          {/* </Tooltip> */}
          {(tenants.length > 0 ? (tenants.length == 1 ? tenantNames : tenants.length) : 'All')}
          <br />
          {/* <Tooltip title={companyNames}> */}
            <span style={{ fontWeight: "bold" }}>Company: </span>
          {/* </Tooltip> */}
          {(companies.length > 0 ? (companies.length == 1 ? companyNames : companies.length) : 'All')}
          <br />
          {/* <Tooltip title={buNames}> */}
            <span style={{ fontWeight: "bold" }}>BU: </span>
          {/* </Tooltip> */}
          {(bus.length > 0 ? (bus.length == 1 ? buNames : bus.length) : 'All')}
          <br />
          {dates?.length > 0 && (<>
            {/* <Tooltip title={dateNames}> */}
              <span style={{ fontWeight: "bold" }}>Date Added: </span>
            {/* </Tooltip> */}
            { (dates?.length == 1 ? dateNames : dates?.length ) }</>)}
        </>
        ) : (<></>)
        }
      </>
    )
  }

  return (
    <>
      <div className="title-block search-block tabel-search">
        {removeSearch !== true && (<Filter onSearch={onFinishSearch} />)}
        <div className="btns-block">
          {tableButtons ? tableButtons() : <></>}
          {!hideExportButton && (
            <Button
              onClick={onExportClick}
              icon={<FileExcelOutlined />}
              loading={loading}
              disabled={reduxStoreData.search.count === 0}
            >
              Export
            </Button>
          )}
          {hideShowHideButton ? (
            <></>
          ) : (
            <Popover content={dropdownMenu} trigger="click" overlayClassName="custom-popover">
              <Button
                disabled={reduxStoreData.search.count === 0}
                icon={
                  <em className="anticon">
                    <img src={`${process.env.PUBLIC_URL}/assets/images/ic-lines.svg`} alt="" />
                  </em>
                }
              >
                Show/Hide Columns
              </Button>
            </Popover>
          )}
          {renderCallApiButton()}
          {showDelete !== false && reduxStoreData.search.count !== 0 && (
            <Popconfirm
              title={confirmOpen === 'delete' && (deleteUpdateTitle())}
              visible={confirmOpen === 'delete'}
              onVisibleChange={(visible) => !visible && setConfirmOpen(null)}
              onConfirm={() => bulkDeleteData()}>
              <Button
                type="primary"
                disabled={reduxStoreData.search.count == 0 || (impactLoading !== null && impactLoading !== 'delete')}
                loading={impactLoading === 'delete'}
                onClick={() => startBulkAction('delete')}
              >
                {Object.keys(selectedRowList).length <= 1
                  ? `Delete All (${dropDownFlag === true
                    ? filterRecordsForLocalSearch?.length
                    : (reduxStoreData.search.count).toLocaleString()
                  })`
                  : `Delete Selected (${Object.keys(selectedRowList).length - 1})`}
              </Button>
            </Popconfirm>
          )}
          {showBulkUpdate && reduxStoreData.search.count !== 0 && (
            <Popconfirm
              title={confirmOpen === 'update' && (deleteUpdateTitle(true))}
              visible={confirmOpen === 'update'}
              onVisibleChange={(visible) => !visible && setConfirmOpen(null)}
              onConfirm={() => setShowSelectedListModal(true)}
            >
              <Button
                type="primary"
                disabled={reduxStoreData.search.count == 0 || (impactLoading !== null && impactLoading !== 'update')}
                loading={impactLoading === 'update'}
                onClick={() => startBulkAction('update')}
              >
                {Object.keys(selectedRowList).length <= 1
                  ? `Update All (${dropDownFlag === true
                    ? filterRecordsForLocalSearch?.length
                    : (reduxStoreData.search.count).toLocaleString()
                  })`
                  : `Update Selected (${Object.keys(selectedRowList).length - 1})`}
              </Button>
            </Popconfirm>
          )}
          {showAddButton && (
            <Button
              type="primary"
              onClick={() => {
                setSelectedId(0);
              }}
            >
              Add
            </Button>
          )}
        </div>
      </div>
      <Form form={form} initialValues={inlineSearch} name="searchTable" onFinish={onFinish}>
        <ReactDragListView {...dragProps}>
          <Table
            rowSelection={!disableRowSelection ? rowSelection : null}
            scroll={{ x: true }}
            rowKey={(record) =>
              record[defaultOrderBy ? defaultOrderBy : 'id'] +
              '-' +
              (record['oauth_id'] ? record['oauth_id'] : '')
            }
            dataSource={
              dropDownFlag === true ? filterRecordsForLocalSearch : reduxStoreData.search.data
            }
            columns={isDragged ? tableColumns : getColumns()}
            loading={
              reduxStoreData.search.loading ||
              reduxStoreData?.delete?.loading ||
              reduxStoreData?.callAllApi?.loading ||
              reduxStoreData?.callApi?.loading ||
              reduxStoreData?.runJobData?.loading
            }
            pagination={
              isStartSchedulaAllApi
                ? {
                  total: reduxStoreData.search.count,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }
                : {
                  ...pagination,
                  pageSizeOptions: ['10', '100', '500', '1000'],
                  showSizeChanger: true,
                  total: reduxStoreData.search.count,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }
            }
            onChange={handleTableChange}
            className="custom-table"
            sortDirections={['ascend', 'descend']}
          />
        </ReactDragListView>
      </Form>
      {dangerImpact && (
        <BulkImpactConfirmModal
          action={dangerImpact.action}
          impact={dangerImpact.impact}
          serverMessage={dangerImpact.serverMessage}
          onConfirm={onDangerConfirm}
          onCancel={() => setDangerImpact(null)}
        />
      )}
    </>
  );
};

export default forwardRef(DataTable);
